import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  AfterViewInit,
  DestroyRef,
  inject,
  Optional,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourseEditorService } from 'core/services/course-editor.service';
import { ToastService } from 'core/services/toast.service';
import {
  Course,
  Module,
  Lesson,
  Question,
} from 'core/models/course-content.model';
import { Notes } from '../notes/notes';
import { HSStaticMethods } from 'preline/non-auto';

@Component({
  selector: 'app-course-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Notes],
  templateUrl: './course-info.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseInfo implements OnInit, AfterViewInit {
  // ============================================================================
  // INPUTS E OUTPUTS
  // ============================================================================

  @Input({ required: true }) variant!: 'course-editor' | 'course-viewer';

  // Props do course-editor
  @Output() courseUpdated = new EventEmitter<void>();

  // Props do course-viewer
  @Input() course: Course | null = null;
  @Input() currentModule: Module | null = null;
  @Input() currentLesson: Lesson | null = null;
  @Input() totalProgress = 0;
  @Input() displayTitle = '';

  // ============================================================================
  // INJEÇÕES DE DEPENDÊNCIAS
  // ============================================================================

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  // Services condicionais
  private courseEditorService: CourseEditorService | null = null;
  private toastService: ToastService | null = null;

  // ============================================================================
  // PROPRIEDADES DO COURSE-EDITOR
  // ============================================================================

  protected courseForm!: FormGroup;
  protected editorCourse: Course | null = null;

  // Estatísticas do curso
  protected totalModules = 0;
  protected totalLessons = 0;
  protected totalQuestions = 0;
  protected totalEvaluationPoints = 0;
  protected totalCourseDuration = 0; // em minutos

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(
    @Optional() courseEditorService: CourseEditorService,
    @Optional() toastService: ToastService,
  ) {
    this.courseEditorService = courseEditorService;
    this.toastService = toastService;
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    if (this.variant === 'course-editor') {
      this.initializeCourseEditor();
    }
  }

  ngAfterViewInit(): void {
    if (this.variant === 'course-editor') {
      // Inicializar tabs do Preline após a view estar pronta
      if (typeof window !== 'undefined') {
        requestAnimationFrame(() => {
          HSStaticMethods.autoInit();
        });
      }
    }
  }

  // ============================================================================
  // MÉTODOS DO COURSE-EDITOR
  // ============================================================================

  private initializeCourseEditor(): void {
    if (!this.courseEditorService) return;

    this.editorCourse = this.courseEditorService.getCourse();
    this.calculateStatistics();

    // Obter lição atual do service
    const navigation = this.courseEditorService.getNavigation();
    this.currentLesson = navigation.currentLesson;

    this.courseForm = this.fb.group({
      title: [
        this.editorCourse?.title || '',
        [Validators.required, Validators.minLength(3)],
      ],
      description: [
        this.editorCourse?.description || '',
        [Validators.required, Validators.minLength(10)],
      ],
      coverImage: [this.editorCourse?.coverImage || ''],
    });

    this.courseEditorService.course$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((course) => {
        this.editorCourse = course;
        this.calculateStatistics();
        if (this.courseForm) {
          this.courseForm.patchValue(
            {
              title: course.title,
              description: course.description,
              coverImage: course.coverImage || '',
            },
            { emitEvent: false },
          );
        }
      });

    // Observar mudanças na lição atual
    this.courseEditorService.lessonChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.courseEditorService) return;
        const navigation = this.courseEditorService.getNavigation();
        this.currentLesson = navigation.currentLesson;
        this.cdr.markForCheck();
      });
  }

  protected calculateStatistics(): void {
    if (!this.editorCourse) {
      this.totalModules = 0;
      this.totalLessons = 0;
      this.totalQuestions = 0;
      this.totalEvaluationPoints = 0;
      this.totalCourseDuration = 0;
      return;
    }

    this.totalModules = this.editorCourse.modules.length;
    this.totalLessons = 0;
    this.totalQuestions = 0;
    this.totalEvaluationPoints = 0;
    this.totalCourseDuration = 0;

    this.editorCourse.modules.forEach((module: Module) => {
      if (module.contentType === 'lessons' && module.lessons) {
        this.totalLessons += module.lessons.length;
        module.lessons.forEach((lesson: Lesson) => {
          this.totalCourseDuration += lesson.duration || 0;
        });
      } else if (module.contentType === 'evaluations' && module.questions) {
        this.totalQuestions += module.questions.length;
        module.questions.forEach((question: Question) => {
          this.totalEvaluationPoints += question.points || 0;
        });
      }
    });
  }

  protected formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}min`;
  }

  protected onSave(): void {
    if (
      this.variant !== 'course-editor' ||
      !this.courseEditorService ||
      !this.toastService
    )
      return;

    if (this.courseForm.invalid) {
      this.courseForm.markAllAsTouched();
      this.toastService.error(
        'Por favor, preencha todos os campos obrigatórios corretamente.',
      );
      return;
    }

    const formValue = this.courseForm.value;
    this.courseEditorService.updateCourse({
      title: formValue.title,
      description: formValue.description,
      coverImage: formValue.coverImage || undefined,
    });

    this.toastService.success('Informações do curso salvas com sucesso!');
    this.courseUpdated.emit();
  }

  protected getFieldError(fieldName: string): string {
    const field = this.courseForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return '';
    }

    if (field.errors['required']) {
      return `${fieldName === 'title' ? 'Título' : 'Descrição'} é obrigatório`;
    }
    if (field.errors['minlength']) {
      const requiredLength = field.errors['minlength'].requiredLength;
      return `${fieldName === 'title' ? 'Título' : 'Descrição'} deve ter pelo menos ${requiredLength} caracteres`;
    }

    return '';
  }

  protected isFieldInvalid(fieldName: string): boolean {
    const field = this.courseForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }
}
